import React, { useState, useEffect } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { createReglementArbitrage } from "../../services/reglementarbitrageServices";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

const initialForm = {
  title: "",
  content: "",
  lien_url: "",
  lien_url_image: "",
};

const CreateReglementArbitrageModal = ({ isOpen, onClose, onCreateSuccess }) => {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset quand on ferme
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialForm);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Merci d’indiquer un titre.");
      return;
    }

    try {
      setIsSubmitting(true);
      await createReglementArbitrage(formData);

      // On laisse le parent gérer le refresh + toasts
      if (onCreateSuccess) {
        onCreateSuccess();
      }
      setFormData(initialForm);
    } catch (error) {
      console.error("Erreur lors de la création du règlement:", error);
      toast.error("Erreur lors de la création du règlement.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData(initialForm);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
      <div className="relative w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200/70 dark:border-slate-700/70">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200/70 dark:border-slate-700/70 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <h3 className="text-base sm:text-lg font-bold uppercase tracking-wide font-montserrat">
            Ajouter un règlement
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-sm"
          >
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <form
          onSubmit={handleSubmit}
          className="px-4 sm:px-6 py-4 space-y-6 text-slate-800 dark:text-slate-100"
        >
          {/* Bloc infos principales */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Informations générales
            </h4>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold">Titre du règlement</label>
              <input
                type="text"
                name="title"
                placeholder="Ex : Règlement IJF – Arbitrage, Consignes CS départementales..."
                value={formData.title}
                onChange={handleChange}
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                required
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold">
                  URL du règlement (PDF ou page)
                </label>
                <input
                  type="url"
                  name="lien_url"
                  placeholder="https://… (facultatif mais recommandé)"
                  value={formData.lien_url}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                />
                <p className="text-[0.7rem] text-slate-500 dark:text-slate-400">
                  Lien vers le document officiel (PDF, page fédérale, IJF…).
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold">
                  URL de l’image d’illustration
                </label>
                <input
                  type="url"
                  name="lien_url_image"
                  placeholder="https://… (optionnel)"
                  value={formData.lien_url_image}
                  onChange={handleChange}
                  className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                />
                <p className="text-[0.7rem] text-slate-500 dark:text-slate-400">
                  Image utilisée comme vignette (ex : couverture du document).
                </p>
              </div>
            </div>
          </div>

          {/* Bloc contenu descriptif */}
          <div className="space-y-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 p-3 sm:p-4 border border-slate-200/60 dark:border-slate-700/60">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300">
              Description / notes
            </h4>
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800">
              <ReactQuill
                value={formData.content}
                onChange={handleContentChange}
                theme="snow"
              />
            </div>
            <p className="text-[0.7rem] text-slate-500 dark:text-slate-400">
              Ajoutez éventuellement un résumé, les points clés, les changements
              de saison, etc.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-3 pt-2 pb-4">
            <Button
              text={isSubmitting ? "Enregistrement..." : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className="btn-primary gap-2"
              disabled={isSubmitting}
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={handleCancel}
              type="button"
              className="btn-ghost gap-2"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReglementArbitrageModal;
